import { Fragment, type ReactElement } from 'react';
import { splitHiddenCharacterExcerpt } from '@kirin/api-client/helpers/alerts';
import { cn } from '@/lib/utils';

/**
 * KIR-4229: the surrounding-text excerpt for an invisible-unicode alert, with
 * the flagged region highlighted.
 *
 * The backend hands over one string in which each invisible run is a
 * `[U+200E]` / `[787 hidden characters: …]` marker. Rendered flat it is still a
 * line of monospace, barely better than the bare offset the ticket complains
 * about; the highlight is what makes the region findable. Amber `mark` matches
 * the MCP tool-description evidence on the same card.
 */
export function HiddenCharacterExcerpt({
  value,
  className,
  'data-testid': testId,
}: {
  value: string;
  className?: string;
  'data-testid'?: string;
}): ReactElement {
  return (
    <span
      className={cn('break-words font-mono', className)}
      data-testid={testId}
    >
      {splitHiddenCharacterExcerpt(value).map((segment, index) =>
        segment.hidden ? (
          <mark
            key={index}
            className="rounded bg-amber-100 px-1 py-0.5 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
            data-testid="hidden-character-marker"
          >
            {segment.text}
          </mark>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        )
      )}
    </span>
  );
}

HiddenCharacterExcerpt.displayName = 'HiddenCharacterExcerpt';
